import { useState, useEffect, useRef } from "react";
import { useToast } from "../contexts/ToastContext";
import { apiService } from "../services/api";

const useTableConfiguration = () => {
  const { showToast } = useToast();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false); // Add loading ref to prevent multiple calls
  const cacheRef = useRef(null); // Add cache ref
  const mountedRef = useRef(true);

  // All available fields from the database table
  const availableFields = [
    { key: "report_id", label: "Report id", type: "standard" },
    { key: "report_type", label: "Report type", type: "standard" },
    { key: "date_of_visit", label: "Date of visit", type: "standard" },
    { key: "part_number", label: "Part number", type: "standard" },
    { key: "description", label: "Description", type: "standard" },
    { key: "failure_reason", label: "Failure reason", type: "standard" },
    { key: "serial_number", label: "Serial number", type: "standard" },
    { key: "site_name", label: "Site name", type: "standard" },
    { key: "rig_id", label: "Rig ID", type: "standard" },
    { key: "rig_model", label: "Rig model", type: "standard" },
    {
      key: "customer_entity",
      label: "Customer account number",
      type: "standard",
    },
    { key: "country_name", label: "Country", type: "standard" },
    { key: "customer_name", label: "Customer name", type: "standard" },
    { key: "sales_area", label: "Sales area", type: "standard" },
    { key: "entity", label: "Entity", type: "standard" },
    { key: "blob_url", label: "Document link", type: "standard" },
    { key: "product_line", label: "Product line", type: "standard" },
    { key: "generated_by", label: "Generated by", type: "standard" },
    { key: "report_as", label: "Int. / ext.", type: "standard" },
  ];

  // Helper function to get default widths
  function getDefaultWidth(fieldKey) {
    const widths = {
      report_id: 120,
      report_type: 80,
      date_of_visit: 100,
      customer_name: 120,
      country_name: 100,
      site_name: 120,
      description: 220,
      serial_number: 120,
      failure_reason: 190,
      part_number: 120,
      rig_model: 110,
      rig_id: 110,
      customer_entity: 150,
      sales_area: 100,
      entity: 100,
      blob_url: 100,
      product_line: 100,
      generated_by: 150,
      report_as: 100,
    };
    return widths[fieldKey] || 110;
  }

  // Default configurations - Always all 4
  const defaultConfigurations = [
    {
      id: "reports_table",
      name: "Rock tool cards (RTC)",
      reportType: "RTC",
      isDefault: true,
      fields: availableFields.map((field, index) => ({
        ...field,
        visible: [
          "date_of_visit",
          "customer_name",
          "country_name",
          "site_name",
          "description",
          "serial_number",
          "failure_reason",
          "part_number",
          "sales_area",
          "part_number",
          "product_line",
          "report_as",
        ].includes(field.key),
        order: index + 1,
        width: getDefaultWidth(field.key),
      })),
    },
    {
      id: "search_results",
      name: "Customer site visits (CSV)",
      reportType: "CSV",
      isDefault: true,
      fields: availableFields.map((field, index) => ({
        ...field,
        visible: [
          "date_of_visit",
          "customer_name",
          "site_name",
          "failure_reason",
          "sales_area",
          "entity",
          "product_line",
          "generated_by",
          "report_as",
        ].includes(field.key),
        order: index + 1,
        width: getDefaultWidth(field.key),
      })),
    },
    {
      id: "dashboard_table",
      name: "Site audits (SA)",
      reportType: "SA",
      isDefault: true,
      fields: availableFields.map((field, index) => ({
        ...field,
        visible: [
          "date_of_visit",
          "customer_name",
          "site_name",
          "report_type",
          "sales_area",
          "entity",
          "product_line",
          "generated_by",
          "report_as",
        ].includes(field.key),
        order: index + 1,
        width: getDefaultWidth(field.key),
      })),
    },
    {
      id: "export_view",
      name: "Rig audits (RA)",
      reportType: "RA",
      isDefault: true,
      fields: availableFields.map((field, index) => ({
        ...field,
        visible: [
          "date_of_visit",
          "customer_name",
          "country_name",
          "site_name",
          "rig_model",
          "rig_id",
          "sales_area",
          "entity",
          "product_line",
          "generated_by",
          "report_as",
        ].includes(field.key),
        order: index + 1,
        width: getDefaultWidth(field.key),
      })),
    },
  ];

  useEffect(() => {
    // Prevent multiple simultaneous loads
    if (loadingRef.current) {
      return;
    }

    // Return cached data if available
    if (cacheRef.current) {
      setConfigurations(cacheRef.current);
      setLoading(false);
      return;
    }

    loadConfigurations();

    // Cleanup function
    return () => {
      mountedRef.current = false;
    };
  }, []); // Keep empty dependency array

  const loadConfigurations = async () => {
    // Prevent multiple simultaneous calls
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true; // Set loading flag
      setLoading(true);

      // Always start with all 4 default configurations
      let finalConfigurations = [...defaultConfigurations];

      // Try to get user customizations from API - single call
      try {
        const response = await apiService.getTableConfigurations();

        if (response && Object.keys(response).length > 0) {
          // Merge API data with defaults - preserve all 4 configs but update with saved settings
          finalConfigurations = defaultConfigurations.map((defaultConfig) => {
            const apiConfig = response[defaultConfig.id];

            if (apiConfig && apiConfig.columns) {
              // Found saved configuration - merge it
              return {
                ...defaultConfig,
                name: apiConfig.name || defaultConfig.name,
                fields: availableFields.map((field, index) => ({
                  ...field,
                  visible: apiConfig.columns.includes(field.key),
                  order: index + 1,
                  width: getDefaultWidth(field.key),
                })),
              };
            }

            // No saved config found - return default
            return defaultConfig;
          });
        }
      } catch (apiError) {
        console.warn("API error, using defaults:", apiError);
        // Keep the default configurations
      }

      // Cache the result
      cacheRef.current = finalConfigurations;
      setConfigurations(finalConfigurations);
    } catch (error) {
      console.error("Configuration loading error:", error);
      const fallbackConfigs = defaultConfigurations;
      cacheRef.current = fallbackConfigs;
      setConfigurations(fallbackConfigs);
    } finally {
      setLoading(false);
      loadingRef.current = false; // Reset loading flag
    }
  };

  // Helper to get report type from configuration key
  const getReportTypeFromKey = (key) => {
    const mapping = {
      reports_table: "RTC",
      search_results: "CSV",
      dashboard_table: "SA",
      export_view: "RA",
    };
    return mapping[key] || "RTC";
  };

  // Get configuration for specific report type
  const getConfigurationForReportType = (reportTypeKey) => {
    const configMapping = {
      RTC: "reports_table",
      CSV: "search_results",
      SA: "dashboard_table",
      RA: "export_view",
    };

    const configId = configMapping[reportTypeKey] || "reports_table";
    const config = configurations.find((c) => c.id === configId);

    if (!config) {
      // console.warn(
      //   `No configuration found for ${reportTypeKey}, using default`
      // );
      return (
        defaultConfigurations.find((c) => c.id === configId) ||
        defaultConfigurations[0]
      );
    }

    return config;
  };

  // Get table headers for specific report type
  const getTableHeaders = (reportTypeKey) => {
    const config = getConfigurationForReportType(reportTypeKey);

    if (!config || !config.fields) {
      console.warn("No config or fields found, using defaults");
      return [];
    }

    // Get visible fields and sort by order
    const visibleFields = config.fields
      .filter((field) => field.visible)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    // Always add actions column at the end
    const headers = visibleFields.map((field) => ({
      key: field.key,
      label: field.label,
      width: field.width || getDefaultWidth(field.key),
      sortable: field.key !== "actions",
    }));

    // Add actions column
    headers.push({
      key: "actions",
      label: "Actions",
      width: 110,
      sortable: false,
    });

    return headers;
  };

  // Clear cache function for when configurations are updated
  const clearCache = () => {
    cacheRef.current = null;
  };

  return {
    configurations,
    loading,
    getConfigurationForReportType,
    getTableHeaders,
    loadConfigurations,
    clearCache,
  };
};

export default useTableConfiguration;
